/**
 * Neutral surface palette shared by components that need a denser, flatter look than the
 * MUI defaults give — hairline borders, a tinted table/rail head, and three text weights.
 *
 * The MUI theme owns brand and semantic color; this owns the greys the theme has no slot
 * for. `accent` mirrors the theme's primary so the two never drift visibly, but is kept
 * here as a literal so this module has no theme dependency and can be used in plain
 * (non-`sx`) style objects.
 */
export const SURFACE = {
  accent: "#5B5FE0",
  accentDark: "#4A4ECF",
  accentTint: "#EEEEFC",
  accentBorder: "#D9DAF8",

  card: "#FFFFFF",
  /** Tinted background for table headers and side rails. */
  head: "#F8F9FC",
  /** Row hover / subtle inset panel. */
  hover: "#FAFBFE",
  /** Strong hairline — header underlines, section splits. */
  line: "#EAECF3",
  /** Soft hairline — row separators, card outlines. */
  line2: "#F2F4F8",

  tx: "#1F2437",
  tx2: "#5B6178",
  tx3: "#98A0B5",
  /** Disabled text — one step lighter than tx3. */
  txDisabled: "#CBD1DD",

  radius: "12px",
  radiusSm: "8px",
  shadow: "0 1px 2px rgba(31,36,55,.04), 0 1px 3px rgba(31,36,55,.03)",
  shadowPopover: "0 12px 32px rgba(31,36,55,.14)",
  focusRing: "0 0 0 3px rgba(91,95,224,.12)",
} as const;

/** Sunday-first, matching JS `Date.getDay()` / dayjs `.day()`. */
const WEEKDAY_COLORS = [
  "#DC4A4A", // Sun
  "#F7B733", // Mon
  "#f9c4d6", // Tue
  "#46B773", // Wed
  "#FF8C00", // Thu
  "#1E88E5", // Fri
  "#8E44AD", // Sat
];

/** Keyed by `dayjs().day()` — 0 = Sunday. */
export const WEEKDAY_COLORS_BY_JS_DAY: Record<number, string> = { ...WEEKDAY_COLORS };

/**
 * Keyed by ISO weekday — 1 = Monday … 7 = Sunday. This is the numbering the platform API
 * uses (`visit_cycle_weekdays`) and the order a Monday-first calendar header renders in.
 */
export const WEEKDAY_COLORS_BY_ISO: Record<number, string> = {
  1: WEEKDAY_COLORS[1],
  2: WEEKDAY_COLORS[2],
  3: WEEKDAY_COLORS[3],
  4: WEEKDAY_COLORS[4],
  5: WEEKDAY_COLORS[5],
  6: WEEKDAY_COLORS[6],
  7: WEEKDAY_COLORS[0],
};
